import { useEffect, useMemo, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Search, Trash2, Users } from "lucide-react";

export default function PartiesPage() {
  const { user } = useAuth();
  const canWrite = ["accounts", "super_admin", "admin"].includes(user?.role);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/customers"); setCustomers(data.customers || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const out = [];
    for (const c of customers) {
      for (const p of (c.consignors || [])) out.push({ customer: c, type: "consignor", party: p });
      for (const p of (c.consignees || [])) out.push({ customer: c, type: "consignee", party: p });
    }
    const needle = q.trim().toLowerCase();
    if (!needle) return out;
    return out.filter(r =>
      r.party.name?.toLowerCase().includes(needle) ||
      r.party.gstin?.toLowerCase().includes(needle) ||
      r.party.phone?.toLowerCase().includes(needle) ||
      r.party.address?.toLowerCase().includes(needle) ||
      r.customer.name?.toLowerCase().includes(needle));
  }, [customers, q]);

  const consignors = rows.filter(r => r.type === "consignor");
  const consignees = rows.filter(r => r.type === "consignee");

  const remove = async (customerId, partyId, name) => {
    if (!window.confirm(`Remove ${name}? This party will no longer be selectable on new LRs.`)) return;
    try { await api.delete(`/customers/${customerId}/parties/${partyId}`); toast.success("Party removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline text-[#FF5A00]">PARTIES DIRECTORY</div>
            <h1 className="font-display font-black text-4xl mt-2 tracking-tight" data-testid="parties-page">Consignors & Consignees</h1>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
              Every party registered against a customer. Add new parties here or inline when creating an LR. Pick a party once and the LR print auto-fills name, address & GSTIN.
            </p>
          </div>
          {canWrite && <AddPartyDialog customers={customers} onDone={load} />}
        </div>

        <div className="mt-6 relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="parties-search"
            placeholder="Search by name, GSTIN, phone, address or customer"
            className="pl-9 h-10 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
        </div>

        <Tabs defaultValue="consignors" className="mt-6">
          <TabsList className="bg-[#141414] border border-[#2A2A2A] rounded-sm p-1">
            <TabsTrigger value="consignors" data-testid="parties-tab-consignors"
              className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">
              Consignors ({consignors.length})
            </TabsTrigger>
            <TabsTrigger value="consignees" data-testid="parties-tab-consignees"
              className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">
              Consignees ({consignees.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="consignors" className="mt-6">
            <PartyTable rows={consignors} canWrite={canWrite} onRemove={remove} />
          </TabsContent>
          <TabsContent value="consignees" className="mt-6">
            <PartyTable rows={consignees} canWrite={canWrite} onRemove={remove} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function PartyTable({ rows, canWrite, onRemove }) {
  return (
    <div className="border border-[#2A2A2A] rounded-sm overflow-x-auto">
      <table className="w-full text-sm min-w-[900px]">
        <thead className="bg-[#141414] text-zinc-400 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left p-3">Name</th>
            <th className="text-left p-3">GSTIN</th>
            <th className="text-left p-3">Phone</th>
            <th className="text-left p-3">Address</th>
            <th className="text-left p-3">Customer</th>
            {canWrite && <th className="text-right p-3">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ customer, party }) => (
            <tr key={party.id} data-testid={`party-row-${party.id}`} className="border-t border-[#2A2A2A] hover:bg-[#141414]/60 align-top">
              <td className="p-3 font-semibold text-white">{party.name}</td>
              <td className="p-3 font-mono text-sky-400 text-xs">{party.gstin || "—"}</td>
              <td className="p-3 font-mono text-xs">{party.phone || "—"}</td>
              <td className="p-3 text-xs text-zinc-400 whitespace-pre-line max-w-[280px]">{party.address || "—"}</td>
              <td className="p-3 text-xs">{customer.name}</td>
              {canWrite && (
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => onRemove(customer.id, party.id, party.name)}
                    data-testid={`party-remove-${party.id}`}
                    className="h-8 rounded-sm border-rose-500/40 bg-transparent text-rose-400 hover:bg-rose-500/10">
                    <Trash2 className="w-3 h-3 mr-1" /> Remove
                  </Button>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={canWrite ? 6 : 5} className="p-10 text-center text-zinc-500">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
              No parties yet. Add one from the "+ New Party" button above.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AddPartyDialog({ customers, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", type: "consignor", name: "", gstin: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);
  const active = (customers || []).filter(c => !c.is_deleted);
  const save = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Pick a customer"); return; }
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const { customer_id, type, ...rest } = form;
      await api.post(`/customers/${customer_id}/parties`, { type, ...rest });
      toast.success(`${type} added`);
      setForm({ customer_id: "", type: "consignor", name: "", gstin: "", phone: "", address: "" });
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-party-btn" className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold h-12 px-5">
          <Plus className="w-5 h-5 mr-2" /> New Party
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#141414] border-[#2A2A2A] sm:max-w-md" data-testid="new-party-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Register consignor / consignee</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid gap-3">
          <div>
            <Label className="overline text-zinc-500">Customer *</Label>
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
              <SelectTrigger data-testid="np-customer" className="mt-1 h-10 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm">
                <SelectValue placeholder="Pick customer this party belongs to" />
              </SelectTrigger>
              <SelectContent>
                {active.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline text-zinc-500">Type *</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="np-type" className="mt-1 h-10 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="consignor">Consignor (sender)</SelectItem>
                <SelectItem value="consignee">Consignee (receiver)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline text-zinc-500">Name *</Label>
            <Input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="np-name" className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm" />
          </div>
          <div>
            <Label className="overline text-zinc-500">GSTIN</Label>
            <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })}
              data-testid="np-gstin" className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm font-mono" />
          </div>
          <div>
            <Label className="overline text-zinc-500">Phone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="np-phone" className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm" />
          </div>
          <div>
            <Label className="overline text-zinc-500">Address</Label>
            <Textarea rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid="np-address" className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}
              className="rounded-sm border-[#2A2A2A] bg-transparent">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="np-save"
              className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">
              {busy ? "Saving…" : "Save party"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
